import merge from 'lodash.merge';
import uniqueArray from 'lodash.uniq';

/**
 * @typedef ComponentTheme - theme data for one component
 * @property {object} tokens - set of default theme tokens
 * @property {object[]} rules - array of rules that apply token overrides if appearance conditions are met
 * @property {object} appearances - set of variants and states that rules may query
 */

/**
 * @typedef Theme
 * @property {object} [metadata] - information about the theme such as name
 * @property {Object.<string,ComponentTheme>} components - set of components themed by this theme
 */

// `lodash.mergeWith` is not suitable here because it applies its callback everywhere in a deep tree
// and is unaware of ancestors. We need to pick custom mergers based on position in the object tree.

/**
 * Merger that doesn't mutate originals, is no-op if only one item is passed, has optional callback.
 */
function cleanMerge(baseObj, newObj, mergeFn = (a, b) => merge({}, a, b)) {
  if (!baseObj) return newObj;
  if (!newObj) return baseObj;
  const mergedObj = mergeFn(baseObj, newObj);
  if (typeof mergedObj === 'object' && mergedObj !== null) {
    if (typeof baseObj === 'string') {
      return Object.values(mergedObj).join('');
    }
    return mergedObj;
  }
  return mergedObj;
}

/**
 * Applies a custom merger to each item in an object of items of the same shape.
 */
function cleanMergeEntries(baseObj, newObj, mergeFn) {
  return cleanMerge(baseObj, newObj, (a, b) => ({
    ...a,
    ...Object.fromEntries(Object.entries(b).map(([key, overrideItem]) => {
      const baseItem = a[key];
      const value = cleanMerge(baseItem, overrideItem, mergeFn);
      return [key, value];
    }))
  }));
}
function mergeAppearances(baseAppearances, subAppearances) {
  return cleanMergeEntries(baseAppearances, subAppearances, ({
    values: baseValues,
    ...baseRest
  }, {
    values: subthemeValues,
    ...subthemeRest
  }) => ({
    // Concatenate values (primitives), removing any duplicates that were defined in both themes
    values: cleanMerge(baseValues, subthemeValues, (a, b) => uniqueArray([...a, ...b])),
    // Simple deep merge for other properties like `type` and `description`
    ...cleanMerge(baseRest, subthemeRest)
  }));
}
function mergeRules(baseRules, subthemeRules) {
  // Concatenate rules so all rules apply but tokens from subtheme rules override those from base theme rules
  return cleanMerge(baseRules, subthemeRules, (a, b) => [...a, ...b]);
}

/**
 * Merges two themes into a combined theme. May be used:
 *  - In NodeJS via system-tokens at build time to build a static theme merged from other themes
 *  - In React via ThemeProvider at run time to apply a set of overrides to a theme
 *
 * @param {Theme} baseTheme - A complete theme that the subtheme is based on
 * @param {Theme} subTheme - A set of theme overrides and extensions that are merged onto baseTheme
 * @returns {Theme}
 */
function mergeThemes({
  components: baseThemeComponents = {},
  ...baseThemeRest
}, {
  components: subThemeComponents = {},
  ...subThemeRest
}) {
  return {
    // Simple deep merge for `metadata` and any other custom properties
    ...cleanMerge(baseThemeRest, subThemeRest),
    // Merge the components lists, applying custom mergers for `rules` and `appearances`.
    components: cleanMergeEntries(baseThemeComponents, subThemeComponents, (baseThemeComponent, subThemeComponent) => {
      const {
        rules: baseRules,
        appearances: baseAppearances,
        ...baseRest
      } = baseThemeComponent;
      const {
        rules: subRules,
        appearances: subAppearances,
        ...subRest
      } = subThemeComponent;
      return {
        appearances: mergeAppearances(baseAppearances, subAppearances),
        rules: mergeRules(baseRules, subRules),
        // Simple deep-merge for `tokens` and any other custom properties
        ...cleanMerge(baseRest, subRest)
      };
    })
  };
}
export default mergeThemes;